/**
 * AccountingPage — the ledger + the live balance equation (Phase 8g.1). ADMIN ONLY.
 *
 * Balances are DERIVED server-side (backend/src/lib/ledger.ts) and returned
 * by GET /api/accounting/balances. The check that must always hold:
 *   Cash + Receivables = Contractors + Commissions + Adrian + Daryl
 * It runs in the background: the Overview shows a red banner only when it fails.
 */

import React, { useMemo, useState } from 'react';
import { CRM, tintOf } from '../lib/theme';
import { numberInput } from '../lib/numberInput';
import { useLeadLabels } from '../contexts/LeadsContext';
import { JobPicker, jobText } from './JobPicker';
import { gate, LoadError } from './States';
import { humanize } from '../lib/feedLabels';
import { useApi, useApiClient } from '../hooks/useApi';
import { useAction, useIntentKey } from '../hooks/useAction';
import { formatMoney, formatDate, formatDateTime, today } from '../lib/utils';
import { Icons } from '../lib/icons';
import { StatementsTab } from './StatementsTab';

const C = { ...CRM, mono: "'DM Mono', monospace" };

type Tab = 'overview' | 'expenses' | 'draws' | 'contractor_payments' | 'commission' | 'adjustments' | 'monthly' | 'statements';

const TABS: { id: Tab; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'expenses', label: 'Expenses' },
  { id: 'draws', label: 'Draws' },
  { id: 'contractor_payments', label: 'Contractor Payments' },
  { id: 'commission', label: 'Sales Commission' },
  { id: 'adjustments', label: 'Corrections' },
  { id: 'monthly', label: 'Monthly View' },
  { id: 'statements', label: 'Statements' },
];

const ACTION_LABEL: Record<string, string> = {
  payment_recognized: 'Customer payment',
  accounting_restated: 'Accounting restated',
  invoice_recognized: 'Invoice generated',
  invoice_voided: 'Invoice voided',
  contractor_approved: 'Contractor approved',
  contractor_repriced: 'Contractor repriced',
  contractor_edited: 'Contractor edited',
  contractor_voided: 'Contractor voided',
  expense_added: 'Expense', expense_updated: 'Expense edited', expense_deleted: 'Expense deleted',
  draw_added: 'Draw', draw_updated: 'Draw edited', draw_deleted: 'Draw deleted',
  adjustment_added: 'Correction', adjustment_updated: 'Correction edited', adjustment_deleted: 'Correction deleted',
  contractor_payment_added: 'Contractor payment', contractor_payment_deleted: 'Contractor payment deleted',
  commission_payment_added: 'Commission payment', commission_payment_deleted: 'Commission payment deleted',
  commission_changed: 'Commission changed',
};

const num = (v: unknown) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const monthLabel = (key: string) => { const [y, m] = key.split('-'); return `${MONTH_NAMES[Number(m) - 1]} ${y}`; };

export function AccountingPage() {
  const api = useApiClient();
  const [tab, setTab] = useState<Tab>('overview');

  const { data: balances, loading: balLoading, error: balError, refetch: refetchBalances } = useApi<any>(async () => (await api.getAccountingBalances()).data);
  const { data: expenses, refetch: refetchExpenses } = useApi<any[]>(async () => (await api.getExpenses()).data || []);
  const { data: draws, refetch: refetchDraws } = useApi<any[]>(async () => (await api.getDraws()).data || []);
  const { data: adjustments, refetch: refetchAdjustments } = useApi<any[]>(async () => (await api.getAdjustments()).data || []);
  const { data: cpayments, refetch: refetchCPayments } = useApi<any[]>(async () => (await api.getContractorPayments()).data || []);
  const { data: ledger, refetch: refetchLedger } = useApi<any>(async () => (await api.getContractorLedger()).data);
  // 049: what each sales rep is owed, and what they have been paid.
  const { data: commLedger, refetch: refetchCommLedger } = useApi<any>(async () => (await api.getCommissionLedger()).data);
  const { data: commPayments, refetch: refetchCommPayments } = useApi<any[]>(async () => (await api.getCommissionPayments()).data || []);
  // 048: card payments with no Square cost — they hold paid jobs on Invoiced.
  const { data: squareNeeded, refresh: refreshSquareNeeded } = useApi<any[]>(async () => { try { return (await api.getSquareCostNeeded()).data || []; } catch { return []; } });
  const { data: categories } = useApi<any[]>(async () => (await api.getExpenseCategories()).data || []);

  const [busy, setBusy] = useState(false);
  // The Overview fetches its own period figures and activity; bumping this refetches them.
  const [version, setVersion] = useState(0);
  const refetchAll = () => { refetchBalances(); setVersion((v) => v + 1); refetchExpenses(); refetchDraws(); refetchAdjustments(); refetchCPayments(); refetchLedger(); refetchCommLedger(); refetchCommPayments(); refreshSquareNeeded().catch(() => {}); };

  /**
   * ONE ACCOUNTING WRITE AT A TIME (useAction's ref guard): a second click while
   * one is in flight is ignored, not queued. Creates also carry the modal's
   * idempotency key, so a repeat of the same intent returns the first record.
   */
  const guard = useAction();
  const run = (fn: () => Promise<void>) =>
    guard.run(async () => {
      setBusy(true);
      try { await fn(); } finally { setBusy(false); }
    }).catch((e: any) => window.alert(e?.message || 'Action failed'));

  // "#4001 · Jane Smith" — the job number first: it is what an adjustment, an
  // expense or a payment is booked against.
  //
  // Labels for exactly the jobs this page mentions (paging 4/4): every lead id
  // in the ledgers and lists, looked up once (POST /leads/lookup) — not the
  // whole lead list, which stopped at 1,000 rows.
  const leadIds = useMemo(() => collectLeadIds([expenses, adjustments, cpayments, ledger, commLedger, commPayments]),
    [expenses, adjustments, cpayments, ledger, commLedger, commPayments]);
  const labels = useLeadLabels(leadIds);
  const leadLabel = (id: string | null) => {
    if (!id) return '—';
    const l = labels.get(id);
    return l ? jobText(l) : '…';
  };

  return (
    /* Full width, like every other page. The 1100px cap left a dead band down
       the right on every tab, and the ledger tables — contractor balances
       especially — are the widest tables in the app. */
    <div>
      {/* MONEY IS WHERE A CONFIDENT ZERO IS WORST. Every figure here is a balance
          somebody might act on, and a failed request rendered them all as nothing
          owed and nothing owing. The lookups behind them (leads, categories) are
          names rather than numbers and do not gate. */}
      {gate('Accounting balances', balLoading, balError, !!balances, refetchBalances)}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '18px', flexWrap: 'wrap' }}>
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{ padding: '7px 14px', background: tab === t.id ? C.panel2 : 'transparent', color: tab === t.id ? C.text : C.muted, border: `1px solid ${tab === t.id ? C.border : 'transparent'}`, borderRadius: '7px', cursor: 'pointer', fontSize: '12px', fontWeight: tab === t.id ? 700 : 400 }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <Overview balances={balances} contractorTotals={ledger?.totals} commissionTotals={commLedger?.totals} version={version}
          squareCost={<SquareCostNeeded rows={squareNeeded ?? []} busy={busy}
            onSave={(paymentId: string, cost: number) => run(async () => { await api.setPaymentSquareCost(paymentId, cost); refetchAll(); })} />} />
      )}
      {tab === 'expenses' && (
        <ExpensesTab rows={expenses} categories={categories} leadLabel={leadLabel} busy={busy}
          onAdd={(d: any, key: string) => run(async () => { await api.withIdempotencyKey(key).addExpense(d); refetchAll(); })}
          onEdit={(id: string, d: any) => run(async () => { await api.updateExpense(id, d); refetchAll(); })}
          onDelete={(id: string) => run(async () => { if (window.confirm('Delete this expense?')) { await api.deleteExpense(id); refetchAll(); } })} />
      )}
      {tab === 'draws' && (
        <DrawsTab rows={draws} busy={busy}
          onAdd={(d: any, key: string) => run(async () => { await api.withIdempotencyKey(key).addDraw(d); refetchAll(); })}
          onEdit={(id: string, d: any) => run(async () => { await api.updateDraw(id, d); refetchAll(); })}
          onDelete={(id: string) => run(async () => { if (window.confirm('Delete this draw?')) { await api.deleteDraw(id); refetchAll(); } })} />
      )}
      {tab === 'adjustments' && (
        <AdjustmentsTab rows={adjustments} leadLabel={leadLabel} busy={busy}
          onAdd={(d: any, key: string) => run(async () => { await api.withIdempotencyKey(key).addAdjustment(d); refetchAll(); })}
          onEdit={(id: string, d: any) => run(async () => { await api.updateAdjustment(id, d); refetchAll(); })}
          onDelete={(id: string) => run(async () => { if (window.confirm('Delete this correction?')) { await api.deleteAdjustment(id); refetchAll(); } })} />
      )}
      {tab === 'contractor_payments' && (
        <ContractorPaymentsTab
          ledger={ledger}
          rows={cpayments}
          leadLabel={leadLabel}
          busy={busy}
          onPay={(d: any, key: string) => run(async () => { await api.withIdempotencyKey(key).payContractor(d); refetchAll(); })}
          onDelete={(id: string) => run(async () => { if (window.confirm('Delete this contractor payment?')) { await api.deleteContractorPayment(id); refetchAll(); } })} />
      )}
      {tab === 'commission' && (
        <CommissionTab
          ledger={commLedger}
          payments={commPayments ?? []}
          leadLabel={leadLabel}
          busy={busy}
          onPay={(d: any, key: string) => run(async () => { await api.withIdempotencyKey(key).payCommission(d); refetchAll(); })}
          onDelete={(id: string) => run(async () => { if (window.confirm('Delete this commission payment?')) { await api.deleteCommissionPayment(id); refetchAll(); } })} />
      )}
      {tab === 'monthly' && <MonthlyTab />}
      {tab === 'statements' && <StatementsTab />}
    </div>
  );
}

// ---- Sales commission (049): the rep ledger ---------------------------------

/**
 * Sales Commission (lib/ledger.ts computeCommissionLedger), the same pattern as
 * Contractor Payments. Per rep: Obligation (unpaid commission on Invoiced jobs),
 * Owed now (on jobs an admin has Closed), Paid ahead (only when someone has
 * some), Balance.
 *
 * A commission reversed after it was paid (invoice voided) leaves its payment
 * with nothing to pay: it covers the rep's other open jobs, and what is left is
 * Paid ahead, netted against the next payment.
 *
 * The balances must add up to the Commissions figure on the Overview; the page
 * says so only when they do not.
 */
function CommissionTab({ ledger, payments, leadLabel, busy, onPay, onDelete }: any) {
  const [payFor, setPayFor] = useState<any | null>(null);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [paidFor, setPaidFor] = useState<any | null>(null);
  const [jobFor, setJobFor] = useState<{ row: any; job: any } | null>(null);
  const rows: any[] = ledger?.rows ?? [];
  const totals = ledger?.totals;
  const rec = ledger?.reconciliation;
  const showAhead = rows.some((r) => num(r.paidAhead) !== 0);
  const cols = 5 + (showAhead ? 1 : 0);
  const paymentsFor = (repId: string) => (payments ?? []).filter((p: any) => String(p.rep_user_id) === String(repId));
  // Commission ids still owed; a payment naming any other was for a reversed one.
  const owedIds = new Set(rows.flatMap((r) => allJobsOf(r).map((j: any) => String(j.commissionId))));

  return (
    <div>
      <TabHeader title="Sales Commission" />

      {rec && !rec.ties && (
        <div style={{ background: `${C.redTint}`, border: `2px solid ${C.redLine}`, color: C.red, borderRadius: '9px', padding: '12px 16px', fontSize: '13px', fontWeight: 700, marginBottom: '14px' }}>
          <Icons.warning size={13} style={{ verticalAlign: 'text-bottom' }} /> Rep balances do not match the books — off by {formatMoney(Math.abs(num(rec.diff)))}.
          <div style={{ fontSize: '11px', fontWeight: 400, marginTop: '4px', fontFamily: C.mono }}>
            Balances {formatMoney(rec.sumOfBalances)} ≠ Commissions on the Overview {formatMoney(rec.commissionsOwed)}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '12px' }}>
        <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '14px 18px', minWidth: '200px' }}>
          <div style={{ fontSize: '10px', color: C.dim, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 700 }}>Total owed to reps</div>
          <div style={{ fontSize: '24px', fontWeight: 700, fontFamily: C.mono, color: num(totals?.balance) > 0 ? C.amber : num(totals?.balance) < 0 ? C.red : C.green, marginTop: '3px' }}>{formatMoney(num(totals?.balance))}</div>
          {(splitLine(totals?.balance, totals) ?? []).map((line) => (
            <div key={line} style={{ fontSize: '11px', color: C.muted, marginTop: '3px', fontFamily: C.mono, whiteSpace: 'nowrap' }}>{line}</div>
          ))}
        </div>
      </div>

      {ledgerPanel(
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <Th>Rep</Th><Th align="right">Obligation</Th><Th align="right">Owed now</Th>
            {showAhead && <Th align="right">Paid ahead</Th>}
            <Th align="right">Balance</Th><Th align="right"></Th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={cols} style={{ padding: '18px', textAlign: 'center', color: C.dim, fontSize: '12px' }}>
                {ledger ? 'No commissions yet. They are recorded when an invoice is issued for a job with a sales rep and a rate.' : 'Loading…'}
              </td></tr>
            )}
            {rows.map((r: any) => {
              const expanded = !!open[r.repUserId];
              const active = activeJobsOf(r);
              const nPaid = paidJobsOf(r).length;
              const nPayments = paymentsFor(r.repUserId).length;
              return (
                <React.Fragment key={r.repUserId}>
                  <tr>
                    <Td>
                      <button onClick={() => setOpen((p) => ({ ...p, [r.repUserId]: !p[r.repUserId] }))}
                        style={{ background: 'none', border: 'none', padding: 0, color: C.text, fontSize: '12px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                        title={expanded ? 'Hide active jobs' : 'Show active jobs'}>
                        <span style={{ color: C.dim, fontSize: '9px' }}>{expanded ? '▼' : '▶'}</span>{r.name}
                        <span style={{ color: C.dim, fontSize: '11px' }}>{active.length ? `· ${active.length} active` : ''}</span>
                      </button>
                    </Td>
                    <Td align="right" mono muted>{moneyOrDash(r.obligation)}</Td>
                    <Td align="right" mono color={r.owedNow > 0 ? C.amber : C.muted}>{moneyOrDash(r.owedNow)}</Td>
                    {showAhead && <Td align="right" mono muted>{num(r.paidAhead) ? formatMoney(r.paidAhead) : ''}</Td>}
                    <Td align="right" mono color={r.balance > 0 ? C.amber : r.balance < 0 ? C.red : C.muted}>{formatMoney(r.balance)}</Td>
                    <Td align="right">
                      <span style={{ display: 'inline-flex', gap: '6px', alignItems: 'center' }}>
                        {(nPaid > 0 || nPayments > 0) && (
                          <button onClick={() => setPaidFor(r)} style={{ ...iconBtn, color: C.accent, fontSize: '11px', fontWeight: 600 }}>Paid jobs{nPaid ? ` (${nPaid})` : ''}</button>
                        )}
                        <button onClick={() => setPayFor(r)} disabled={busy} style={{ ...addBtn, padding: '5px 10px', fontSize: '11px' }}>Record payment</button>
                      </span>
                    </Td>
                  </tr>
                  {expanded && (
                    <tr><td colSpan={cols} style={{ padding: '6px 12px 12px 30px', background: C.panel2 }}>
                      {active.length === 0 && <div style={{ fontSize: '11px', color: C.dim, padding: '6px 0' }}>No active jobs.</div>}
                      {active.map((j: any) => (
                        <JobLine key={j.commissionId} job={j} leadLabel={leadLabel} onOpen={() => setJobFor({ row: r, job: j })}
                          detail={rateText(j)} />
                      ))}
                    </td></tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      )}

      {payFor && (
        <PayCommissionModal rep={payFor} busy={busy} leadLabel={leadLabel}
          onSave={(d: any, key: string) => { onPay(d, key); setPayFor(null); }} onClose={() => setPayFor(null)} />
      )}
      {paidFor && (
        <PaidJobsModal row={paidFor} jobs={paidJobsOf(paidFor)} leadLabel={leadLabel} busy={busy}
          payments={groupSplitPayments(paymentsFor(paidFor.repUserId).map((p: any) => {
            const job = allJobsOf(paidFor).find((j: any) => String(j.commissionId) === String(p.commission_id ?? ''));
            const reversed = p.commission_id && !owedIds.has(String(p.commission_id));
            return { ...p, lead_id: job?.leadId ?? null, label: job ? undefined : reversed ? 'Commission since reversed (invoice voided)' : 'On account' };
          }), leadLabel)}
          jobKey={(j: any) => j.commissionId}
          onOpenJob={(j: any) => setJobFor({ row: paidFor, job: j })} onDelete={onDelete} onClose={() => setPaidFor(null)} />
      )}
      {jobFor && (
        jobFor.job.opening ? <OpeningBreakdown row={jobFor.row} job={jobFor.job} onClose={() => setJobFor(null)} /> :
        <ModalFrame title={leadLabel(jobFor.job.leadId)} onClose={() => setJobFor(null)}
          sub={<span style={{ display: 'inline-flex', gap: '8px', alignItems: 'center' }}>{jobFor.row.name} · invoiced {formatDate(jobFor.job.date)} <StatusChip job={jobFor.job} /></span>}>
          <BreakdownLine label="Commission base" v={num(jobFor.job.base)} muted />
          <BreakdownLine label="Rate" text={rateText(jobFor.job)} muted />
          <BreakdownLine label="Commission" v={num(jobFor.job.amount)} strong />
          <JobPaidLines job={jobFor.job}
            payments={paymentsFor(jobFor.row.repUserId).filter((p: any) => String(p.commission_id ?? '') === String(jobFor.job.commissionId))}
            link={{ href: `/lead/${encodeURIComponent(jobFor.job.leadId)}?tab=finalize`, label: "Open the job's Finalize tab" }} />
        </ModalFrame>
      )}
    </div>
  );
}

/**
 * A payment split over several jobs (063) is one payment: its rows (one per
 * job, one on account) are listed as one line naming each part, and deleting
 * it deletes them all (the server deletes the whole group).
 */
function groupSplitPayments(rows: any[], leadLabel: (id: string) => string): any[] {
  const out: any[] = [];
  const groups = new Map<string, any>();
  for (const p of rows) {
    if (!p.payment_group) { out.push(p); continue; }
    const part = `${p.lead_id ? leadLabel(p.lead_id) : (p.label ?? 'On account')} ${formatMoney(p.amount)}`;
    const g = groups.get(p.payment_group);
    if (g) { g.amount = Math.round((num(g.amount) + num(p.amount)) * 100) / 100; g.label += ` · ${part}`; continue; }
    const first = { ...p, lead_id: null, label: part };
    groups.set(p.payment_group, first);
    out.push(first);
  }
  return out;
}

const rateText = (j: any) => j.opening ? 'Carried from the books' :
  `${j.method === 'flat' ? 'Flat amount' : `${num(j.pct)}% of ${formatMoney(num(j.base))}`}${j.source === 'override' ? ' · override' : ''}`;

/**
 * Pay a rep — one payment, split over several jobs (063).
 *
 * The rep's open jobs are listed with what is left on each. Typing the amount
 * allocates it: owed-now jobs first, oldest first, each up to what is left;
 * the rest is on account. Ticking, unticking or editing a job's amount is the
 * admin's override (typing the amount again re-allocates from scratch). No job
 * may take more than is left on it, and the jobs may not take more than the
 * payment. The opening balance is not listed: on-account money covers it.
 */
function PayCommissionModal({ rep, busy, leadLabel, onSave, onClose }: any) {
  // What is due: Owed now (Closed jobs). A reversed commission already paid has
  // been netted into the figures (Paid ahead), so nothing more is taken off.
  const suggested = Math.max(0, Number(rep.owedNow) || 0);
  const jobs: any[] = useMemo(() => [...(rep.jobs ?? [])]
    .filter((j: any) => !j.opening && num(j.remaining) > 0.004)
    .sort((a: any, b: any) => (a.status === 'closed' ? 0 : 1) - (b.status === 'closed' ? 0 : 1) || String(a.date ?? '').localeCompare(String(b.date ?? ''))),
  [rep]);
  const autoAllocate = (total: number): Record<string, string> => {
    const out: Record<string, string> = {};
    let left = Math.round(total * 100) / 100;
    for (const j of jobs) {
      if (left < 0.005) break;
      const take = Math.min(left, num(j.remaining));
      out[j.commissionId] = take.toFixed(2);
      left = Math.round((left - take) * 100) / 100;
    }
    return out;
  };
  const [amount, setAmount] = useState(suggested > 0 ? suggested.toFixed(2) : '');
  const [alloc, setAlloc] = useState<Record<string, string>>(() => autoAllocate(suggested));
  const [paidOn, setPaidOn] = useState(new Date().toISOString().slice(0, 10));
  const [method, setMethod] = useState('');
  const [notes, setNotes] = useState('');
  const intentKey = useIntentKey();
  const n = Number(amount) || 0;
  const r2 = (x: number) => Math.round(x * 100) / 100;
  const allocated = r2(Object.values(alloc).reduce((t, v) => t + (Number(v) || 0), 0));
  const onAccount = r2(n - allocated);
  const after = r2((Number(rep.balance) || 0) - n);
  const overJob = jobs.find((j) => (Number(alloc[j.commissionId]) || 0) - num(j.remaining) >= 0.005);
  const overTotal = allocated - n >= 0.005;
  const canSave = n > 0 && !overJob && !overTotal && !busy;

  const typeAmount = (v: string) => { setAmount(v); setAlloc(autoAllocate(Number(v) || 0)); };
  const tick = (j: any, on: boolean) => setAlloc((p) => {
    const next = { ...p };
    if (!on) { delete next[j.commissionId]; return next; }
    const used = Object.entries(next).reduce((t, [k, v]) => t + (k === j.commissionId ? 0 : Number(v) || 0), 0);
    next[j.commissionId] = Math.max(0, Math.min(num(j.remaining), r2(n - used))).toFixed(2);
    return next;
  });

  const input: React.CSSProperties = { width: '100%', padding: '7px 9px', background: C.panel2, border: `1px solid ${C.border}`, color: C.text, borderRadius: '6px', fontSize: '12px' };
  const label: React.CSSProperties = { fontSize: '10px', color: C.muted, display: 'block', marginBottom: '3px' };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'var(--crm-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
      <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: '12px', padding: '18px', width: '600px', maxWidth: '92vw', maxHeight: '90dvh', overflowY: 'auto' }}>
        <h3 style={{ fontSize: '14px', fontWeight: 700, color: C.text, marginBottom: '4px' }}>Pay commission — {rep.name}</h3>
        <div style={{ fontSize: '11px', color: Number(rep.balance) < 0 ? C.red : C.muted, marginBottom: '14px' }}>
          {Number(rep.balance) < 0
            ? `Overpaid ${formatMoney(-rep.balance)} (a commission was reversed after it was paid). It is netted here: nothing is suggested until the rep earns past it.`
            : `Owed now ${formatMoney(rep.owedNow)} · Obligation ${formatMoney(rep.obligation)} · Balance ${formatMoney(rep.balance)}`}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '10px' }}>
          <div><label style={label}>Amount</label><input style={input} {...numberInput(1)} value={amount} onChange={(e) => typeAmount(e.target.value)} autoFocus /></div>
          <div><label style={label}>Date paid</label><input style={input} type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} /></div>
          <div><label style={label}>Method</label><input style={input} value={method} onChange={(e) => setMethod(e.target.value)} placeholder="e-Transfer" /></div>
        </div>

        <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: '10px', marginBottom: '10px' }}>
          <div style={{ fontSize: '10px', color: C.dim, textTransform: 'uppercase', fontWeight: 700, marginBottom: '6px' }}>Apply to jobs</div>
          {jobs.length === 0 && <div style={{ fontSize: '11px', color: C.dim }}>No open jobs with anything left — the payment goes on account.</div>}
          {jobs.map((j) => {
            const v = alloc[j.commissionId];
            const on = v != null;
            const over = on && (Number(v) || 0) - num(j.remaining) >= 0.005;
            return (
              <div key={j.commissionId} style={{ display: 'grid', gridTemplateColumns: '18px minmax(0, 1fr) 84px 92px', gap: '8px', alignItems: 'center', fontSize: '12px', padding: '4px 0', borderBottom: `1px solid ${C.border}` }}>
                <input type="checkbox" checked={on} onChange={(e) => tick(j, e.target.checked)} aria-label={`Apply to ${leadLabel(j.leadId)}`} />
                <span style={{ minWidth: 0 }}>
                  <span style={{ color: C.text }}>{leadLabel(j.leadId)}</span>
                  <span style={{ color: j.status === 'closed' ? C.amber : C.dim, fontSize: '10px', marginLeft: '6px' }}>{j.status === 'closed' ? 'owed now' : 'obligation'}</span>
                </span>
                <span style={{ fontFamily: C.mono, color: C.muted, textAlign: 'right' }} title="Left on this job">{formatMoney(j.remaining)}</span>
                <input {...numberInput(1)} min={0} disabled={!on} value={on ? v : ''} placeholder="—"
                  onChange={(e) => setAlloc((p) => ({ ...p, [j.commissionId]: e.target.value }))}
                  style={{ ...input, padding: '5px 7px', fontFamily: C.mono, textAlign: 'right', borderColor: over ? C.redLine : C.border }} />
              </div>
            );
          })}
        </div>

        <div style={{ marginBottom: '10px' }}><label style={label}>Notes</label><input style={input} value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
        <div style={{ fontSize: '11px', fontFamily: C.mono, marginBottom: '4px', color: C.muted }}>
          Allocated <span style={{ color: overTotal ? C.red : C.text }}>{formatMoney(allocated)}</span>
          {' · '}On account <span style={{ color: onAccount < -0.004 ? C.red : C.text }}>{formatMoney(Math.max(0, onAccount))}</span>
          {' · '}Balance after <span style={{ color: after < 0 ? C.red : C.text }}>{after < 0 ? `overpaid ${formatMoney(-after)}` : formatMoney(after)}</span>
        </div>
        {(overJob || overTotal) && (
          <div style={{ fontSize: '11px', color: C.red, marginBottom: '4px' }}>
            {overJob ? `${leadLabel(overJob.leadId)}: only ${formatMoney(overJob.remaining)} is left on that job.` : `The jobs add up to more than the payment.`}
          </div>
        )}
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '12px' }}>
          <button onClick={onClose} style={{ padding: '7px 13px', background: C.panel2, color: C.muted, border: `1px solid ${C.border}`, borderRadius: '7px', cursor: 'pointer', fontSize: '12px' }}>Cancel</button>
          <button disabled={!canSave} onClick={() => onSave({
            rep_user_id: rep.repUserId, amount: r2(n), paid_on: paidOn, method: method || null, notes: notes || null,
            allocations: jobs.filter((j) => (Number(alloc[j.commissionId]) || 0) > 0).map((j) => ({ commission_id: j.commissionId, amount: r2(Number(alloc[j.commissionId])) })),
          }, intentKey)}
            style={{ ...addBtn, opacity: canSave ? 1 : 0.5 }}>Record payment</button>
        </div>
      </div>
    </div>
  );
}

// ---- Square cost needed (048) -----------------------------------------------

/**
 * Card payments with no Square cost recorded. Each one leaves the Processor
 * Fee expense unbooked, so the list sits above the balances. Entered here or on the lead's Finalize tab.
 */
function SquareCostNeeded({ rows, busy, onSave }: { rows: any[]; busy: boolean; onSave: (paymentId: string, cost: number) => void }) {
  const [vals, setVals] = useState<Record<string, string>>({});
  if (!rows.length) return null;
  return (
    <div style={{ background: `${C.amberTint}`, border: `1px solid ${C.amberSoftLine}`, borderRadius: '10px', padding: '12px 14px', marginBottom: '16px' }}>
      <div style={{ fontSize: '12px', fontWeight: 700, color: C.amber, marginBottom: '4px' }}>
        Square cost needed — {rows.length} card payment{rows.length === 1 ? '' : 's'}
      </div>
      <div style={{ fontSize: '11px', color: C.muted, marginBottom: '8px' }}>
        Enter what Square charged (from Square's report). Each is booked as a Processor Fee expense.
      </div>
      {rows.map((r) => {
        const v = vals[r.paymentId] ?? Number(r.prefill).toFixed(2);
        const n = Number(v);
        const ok = v.trim() !== '' && Number.isFinite(n) && n >= 0;
        return (
          <div key={r.paymentId} style={{ display: 'flex', gap: '10px', alignItems: 'center', fontSize: '12px', padding: '4px 0', flexWrap: 'wrap' }}>
            <span style={{ fontFamily: C.mono, color: C.muted, minWidth: '90px' }}>{formatDate(r.paidOn)}</span>
            <span style={{ color: C.text, minWidth: '200px' }}>{r.leadName || '—'} <span style={{ color: C.dim }}>{r.jobNo} · {r.stage ?? '—'}</span></span>
            <span style={{ fontFamily: C.mono, color: C.text }}>charged {formatMoney(r.charged)}</span>
            <input {...numberInput(1)} min={0} value={v}
              onChange={(e) => setVals((p) => ({ ...p, [r.paymentId]: e.target.value }))}
              style={{ width: '90px', background: C.panel2, border: `1px solid ${C.border}`, borderRadius: '6px', color: C.text, fontSize: '12px', padding: '4px 7px', fontFamily: C.mono }} />
            <button disabled={!ok || busy} onClick={() => onSave(r.paymentId, Math.round(n * 100) / 100)}
              style={{ ...addBtn, padding: '4px 10px', fontSize: '11px', opacity: ok ? 1 : 0.5 }}>Record</button>
          </div>
        );
      })}
    </div>
  );
}

// ---- Overview --------------------------------------------------------------

type Period = 'month' | 'ytd' | 'all';
const PERIODS: { id: Period; label: string }[] = [
  { id: 'month', label: 'This Month' },
  { id: 'ytd', label: 'YTD' },
  { id: 'all', label: 'All time' },
];

/**
 * Top row: balances as they stand now, whatever the period. Below it the period
 * filter, which drives the four period cards and Recent Activity only. The
 * balance check runs in the background; the page mentions it only when it fails.
 */
function Overview({ balances, contractorTotals, commissionTotals, version, squareCost }: {
  balances: any; contractorTotals: any; commissionTotals: any; version: number; squareCost: React.ReactNode;
}) {
  const [period, setPeriod] = useState<Period>('month');
  const [showAll, setShowAll] = useState(false);
  const { data: fig, error: figError, refetch: refetchFig } = useApi<any>(async (api) => (await api.getAccountingPeriod(period)).data, { dependencies: [period, version] });
  const { data: act, error: actError, refetch: refetchAct } = useApi<any>(async (api) => (await api.getAccountingActivity(period, 8)).data, { dependencies: [period, version] });
  const b = balances ?? { cash: 0, receivables: 0, obligations: 0, commissionsOwed: 0, adrian: 0, daryl: 0, balanced: true, diff: 0 };
  const label = fig?.label ?? act?.label ?? '';

  return (
    <div>
      {!b.balanced && (
        <div style={{ background: `${C.redTint}`, border: `2px solid ${C.redLine}`, color: C.red, borderRadius: '9px', padding: '14px 16px', fontSize: '14px', fontWeight: 700, marginBottom: '16px' }}>
          <Icons.warning size={13} style={{ verticalAlign: 'text-bottom' }} /> Books out of balance by {formatMoney(Math.abs(num(b.diff)))} — check recent entries.
          <div style={{ fontSize: '11px', fontWeight: 400, marginTop: '4px', fontFamily: C.mono }}>
            Cash {formatMoney(b.cash)} + to collect {formatMoney(b.receivables)} = {formatMoney(b.bank)} ≠ Contractors {formatMoney(b.obligations)} + Commissions {formatMoney(b.commissionsOwed)} + Adrian {formatMoney(b.adrian)} + Daryl {formatMoney(b.daryl)} = {formatMoney(b.sum)}
          </div>
        </div>
      )}

      {squareCost}

      {/* Always current */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '12px', marginBottom: '22px' }}>
        <BalanceCard label="Bank Balance" value={b.cash} hint="Cash on hand"
          sub={num(b.receivables) ? [`+ ${formatMoney(b.receivables)} to collect`] : undefined} />
        <BalanceCard label="Contractors" value={b.obligations} hint="Owed to contractors" neutralPositive
          sub={splitLine(b.obligations, contractorTotals)} />
        <BalanceCard label="Commissions" value={b.commissionsOwed} hint="Owed to sales reps" neutralPositive
          sub={splitLine(b.commissionsOwed, commissionTotals)} />
        {/* NO PERCENTAGE IN THE LABEL. The split is date-effective, so any number
            here would be wrong for part of the balance it sits above. The
            schedule is in Settings → Pricing & billing. */}
        <BalanceCard label="Adrian" value={b.adrian} hint="Outstanding share — assumes all money owed to and by the company is settled" />
        <BalanceCard label="Daryl" value={b.daryl} hint="Outstanding share — assumes all money owed to and by the company is settled" />
      </div>

      {/* Period filter */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: '2px', background: C.panel, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '2px' }}>
          {PERIODS.map((p) => (
            <button key={p.id} onClick={() => setPeriod(p.id)}
              style={{ padding: '5px 12px', background: period === p.id ? C.panel2 : 'transparent', color: period === p.id ? C.text : C.muted, border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: period === p.id ? 700 : 400 }}>
              {p.label}
            </button>
          ))}
        </div>
        <span style={{ fontSize: '13px', fontWeight: 700, color: C.text }}>{label}</span>
      </div>

      {figError && <LoadError what="Period figures" error={figError} onRetry={refetchFig} />}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '12px', marginBottom: '22px', opacity: fig ? 1 : 0.5 }}>
        <BalanceCard label="Revenue" value={fig?.revenue ?? 0} hint={label} neutralPositive />
        <BalanceCard label="Profit" value={fig?.profit ?? 0} hint="After pay, commission, expenses"
          sub={fig ? [`Contractor pay ${formatMoney(fig.contractorCost)}`, `Commission ${formatMoney(fig.commission)}`] : undefined} />
        <BalanceCard label="Expenses" value={fig?.expenses ?? 0} hint={label} neutralPositive />
        <BalanceCard label="Draws" value={fig?.draws ?? 0} hint={label} neutralPositive
          sub={fig && num(fig.draws) ? [`Adrian ${formatMoney(fig.drawsAdrian)}`, `Daryl ${formatMoney(fig.drawsDaryl)}`] : undefined} />
      </div>

      {/* Recent activity, by accounting date */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <h3 style={{ fontSize: '11px', fontWeight: 700, color: C.dim, textTransform: 'uppercase' }}>Recent Activity · {label}</h3>
        {num(act?.total) > 0 && (
          <button onClick={() => setShowAll(true)} style={{ ...iconBtn, color: C.accent, fontWeight: 600 }}>View all ({act.total})</button>
        )}
      </div>
      {actError && <LoadError what="Recent activity" error={actError} onRetry={refetchAct} />}
      <ActivityTable rows={act?.rows ?? null} />
      {showAll && <ActivityModal period={period} label={label} version={version} onClose={() => setShowAll(false)} />}
    </div>
  );
}

/** "Obligation $X" / "Owed now $Y", one per line, plus anything else in the figure so the lines add up. */
function splitLine(total: unknown, t: any): string[] | undefined {
  if (!t) return undefined;
  const parts = [`Obligation ${formatMoney(num(t.obligation))}`, `Owed now ${formatMoney(num(t.owedNow))}`];
  if (num(t.paidAhead)) parts.push(`Paid ahead −${formatMoney(num(t.paidAhead))}`);
  const rest = round2(num(total) - (num(t.obligation) + num(t.owedNow) - num(t.paidAhead)));
  if (rest) parts.push(`Corrections ${formatMoney(rest)}`);
  return parts;
}

/**
 * A log line, shortened: the Type column already says what it was, so the
 * description drops the repeated lead-in, and "— job #1042" becomes "· #1042".
 * The full text is on hover.
 */
function shortDesc(d: string | null | undefined): string {
  if (!d) return '—';
  return d
    .replace(/^(Expense|Draw|Adjustment):\s*/, '')
    .replace(/ recognized(?= —|$)/, '')
    .replace(/\s+—\s+job\s+#?/g, ' · #')
    .trim();
}

function ActivityTable({ rows, showRecorded }: { rows: any[] | null; showRecorded?: boolean }) {
  const cols = ['Date', ...(showRecorded ? ['Recorded'] : []), 'Type', 'Description', 'Cash Δ', 'Profit Δ'];
  return (
    <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: '10px', overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'auto' }}>
        <thead>
          <tr>{cols.map((h, i) => <Th key={h} align={i >= cols.length - 2 ? 'right' : 'left'}>{h}</Th>)}</tr>
        </thead>
        <tbody>
          {rows && rows.length === 0 && (
            <tr><td colSpan={cols.length} style={{ padding: '18px', textAlign: 'center', color: C.dim, fontSize: '12px' }}>No activity in this period.</td></tr>
          )}
          {!rows && (
            <tr><td colSpan={cols.length} style={{ padding: '18px', textAlign: 'center', color: C.dim, fontSize: '12px' }}>Loading…</td></tr>
          )}
          {(rows ?? []).map((a) => {
            const profitD = num(a.profit_delta);
            const cashD = a.cash_delta;
            return (
              <tr key={a.id}>
                <Td mono muted>{formatDate(a.accounting_date)}</Td>
                {showRecorded && <Td mono muted>{formatDateTime(a.occurred_at)}</Td>}
                <Td>{ACTION_LABEL[a.action] ?? humanize(a.action)}</Td>
                <td title={a.description || ''} style={{ padding: '8px 12px', fontSize: '12px', color: C.muted, borderBottom: `1px solid ${C.panel2}`, maxWidth: showRecorded ? undefined : '420px', whiteSpace: showRecorded ? 'normal' : 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {showRecorded ? (a.description || '—') : shortDesc(a.description)}
                </td>
                {cashD == null
                  ? <td title="Not recorded for this older entry" style={{ padding: '8px 12px', fontSize: '12px', textAlign: 'right', color: C.dim, borderBottom: `1px solid ${C.panel2}` }}>n/a</td>
                  : <Td align="right" mono color={num(cashD) < 0 ? C.red : C.text}>{deltaStr(cashD)}</Td>}
                <Td align="right" mono color={profitD < 0 ? C.red : C.text}>{deltaStr(profitD)}</Td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const ACTIVITY_PAGE = 25;

/** The full log for the period, by accounting date, a page at a time. */
function ActivityModal({ period, label, version, onClose }: { period: Period; label: string; version: number; onClose: () => void }) {
  const [page, setPage] = useState(0);
  const { data, error, refetch } = useApi<any>(async (api) => (await api.getAccountingActivity(period, ACTIVITY_PAGE, page * ACTIVITY_PAGE)).data, { dependencies: [period, page, version] });
  const total = num(data?.total);
  const pages = Math.max(1, Math.ceil(total / ACTIVITY_PAGE));
  const navBtn = (disabled: boolean): React.CSSProperties => ({ padding: '5px 11px', background: C.panel2, color: disabled ? C.dim : C.text, border: `1px solid ${C.border}`, borderRadius: '6px', cursor: disabled ? 'default' : 'pointer', fontSize: '12px' });
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'var(--crm-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: '12px', padding: '18px', width: '960px', maxWidth: '94vw', maxHeight: '88vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: 700, color: C.text }}>Activity · {label}</h3>
          <button onClick={onClose} style={{ ...iconBtn, fontSize: '16px' }}>✕</button>
        </div>
        <div style={{ fontSize: '11px', color: C.muted, marginBottom: '12px' }}>
          By accounting date: a payment on its paid date, an invoice on its invoice date. "Recorded" is when it was entered.
        </div>
        {error && <LoadError what="Activity" error={error} onRetry={refetch} />}
        <div style={{ overflowY: 'auto', flex: 1 }}>
          <ActivityTable rows={data?.rows ?? null} showRecorded />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px', fontSize: '12px', color: C.muted }}>
          <span>{total ? `${page * ACTIVITY_PAGE + 1}–${Math.min(total, (page + 1) * ACTIVITY_PAGE)} of ${total}` : ''}</span>
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            <button disabled={page === 0} onClick={() => setPage((p) => p - 1)} style={navBtn(page === 0)}>‹ Newer</button>
            <span>Page {page + 1} of {pages}</span>
            <button disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)} style={navBtn(page + 1 >= pages)}>Older ›</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function BalanceCard({ label, value, hint, sub, neutralPositive }: { label: string; value: number; hint: string; sub?: string[]; neutralPositive?: boolean }) {
  const n = num(value);
  const color = n < 0 ? C.red : neutralPositive ? C.text : n > 0 ? C.green : C.text;
  return (
    <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '14px 16px' }}>
      <div style={{ fontSize: '10px', color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>{label}</div>
      <div style={{ fontSize: '22px', fontWeight: 700, fontFamily: C.mono, color }}>{formatMoney(n)}</div>
      {sub?.map((line) => (
        <div key={line} style={{ fontSize: '11px', color: C.muted, marginTop: '3px', fontFamily: C.mono, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{line}</div>
      ))}
      {/* Wraps rather than cutting off: the partner tiles' hint is a sentence. */}
      <div title={hint} style={{ fontSize: '10px', color: C.dim, marginTop: '5px', lineHeight: 1.35, maxWidth: '240px' }}>{hint}</div>
    </div>
  );
}

function deltaStr(v: unknown) {
  const n = num(v);
  if (n === 0) return '—';
  return (n > 0 ? '+' : '−') + formatMoney(Math.abs(n)).replace('$', '$');
}

// ---- Shared table bits ------------------------------------------------------

function Td({ children, align, mono, muted, color }: { children: React.ReactNode; align?: 'left' | 'right'; mono?: boolean; muted?: boolean; color?: string }) {
  return <td style={{ padding: '8px 12px', fontSize: '12px', textAlign: align ?? 'left', fontFamily: mono ? C.mono : undefined, color: color ?? (muted ? C.muted : C.text), borderBottom: `1px solid ${C.panel2}` }}>{children}</td>;
}

function Th({ children, align }: { children?: React.ReactNode; align?: 'left' | 'right' }) {
  return <th style={{ padding: '8px 12px', fontSize: '9px', fontWeight: 700, color: C.dim, textTransform: 'uppercase', textAlign: align ?? 'left', borderBottom: `1px solid ${C.border}` }}>{children}</th>;
}

function ledgerPanel(children: React.ReactNode) {
  return <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: '10px', overflow: 'hidden' }}>{children}</div>;
}

const addBtn: React.CSSProperties = { padding: '7px 13px', background: C.accentFill, color: 'var(--crm-on-fill)', border: 'none', borderRadius: '7px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 };
const iconBtn: React.CSSProperties = { background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: C.muted };

function TabHeader({ title, onAdd, addLabel }: { title: string; onAdd?: () => void; addLabel?: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
      <h3 style={{ fontSize: '11px', fontWeight: 700, color: C.dim, textTransform: 'uppercase' }}>{title}</h3>
      {onAdd && <button onClick={onAdd} style={addBtn}>{addLabel}</button>}
    </div>
  );
}

// ---- Expenses --------------------------------------------------------------

function ExpensesTab({ rows, categories, leadLabel, busy, onAdd, onEdit, onDelete }: any) {
  const [modal, setModal] = useState<{ mode: 'add' | 'edit'; row?: any } | null>(null);
  return (
    <div>
      <TabHeader title="Expenses" onAdd={() => setModal({ mode: 'add' })} addLabel="+ Add expense" />
      {ledgerPanel(
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><Th>Date</Th><Th>Category</Th><Th>Description</Th><Th>Lead</Th><Th align="right">Amount</Th><Th align="right"></Th></tr></thead>
          <tbody>
            {(!rows || rows.length === 0) && <tr><td colSpan={6} style={{ padding: '18px', textAlign: 'center', color: C.dim, fontSize: '12px' }}>No expenses yet.</td></tr>}
            {(rows ?? []).map((r: any) => (
              <tr key={r.id}>
                <Td mono muted>{formatDate(r.spent_on)}</Td>
                <Td>{r.category || '—'}</Td>
                <Td>{r.description || '—'}</Td>
                <Td muted>{leadLabel(r.lead_id)}</Td>
                <Td align="right" mono color={num(r.amount) < 0 ? C.green : C.text}>{formatMoney(r.amount)}</Td>
                <Td align="right"><span style={{ display: 'inline-flex', gap: '8px' }}>
                  <button style={iconBtn} onClick={() => setModal({ mode: 'edit', row: r })} aria-label="Edit"><Icons.edit size={15} /></button>
                  <button style={{ ...iconBtn, color: C.red }} onClick={() => onDelete(r.id)} aria-label="Delete"><Icons.delete size={15} /></button>
                </span></Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {modal && (
        <LedgerModal title={modal.mode === 'add' ? 'Add expense' : 'Edit expense'} busy={busy} onClose={() => setModal(null)}
          fields={[
            { key: 'spent_on', label: 'Date', type: 'date', default: modal.row?.spent_on ?? today() },
            { key: 'category', label: 'Category', type: 'select', options: (categories ?? []).map((c: any) => c.value), default: modal.row?.category ?? '' },
            { key: 'description', label: 'Description', type: 'text', default: modal.row?.description ?? '' },
            { key: 'amount', label: 'Amount (negative = refund)', type: 'number', default: modal.row?.amount ?? '' },
            { key: 'lead_id', label: 'Job # (optional — counts in that job\'s Margin)', type: 'job', default: modal.row?.lead_id ?? '' },
            { key: 'notes', label: 'Notes', type: 'text', default: modal.row?.notes ?? '' },
          ]}
          onSave={(vals, key) => { if (modal.mode === 'add') onAdd(vals, key); else onEdit(modal.row.id, vals); setModal(null); }} />
      )}
    </div>
  );
}

// ---- Draws -----------------------------------------------------------------

function DrawsTab({ rows, busy, onAdd, onEdit, onDelete }: any) {
  const [modal, setModal] = useState<{ mode: 'add' | 'edit'; row?: any } | null>(null);
  return (
    <div>
      <TabHeader title="Partner Draws" onAdd={() => setModal({ mode: 'add' })} addLabel="+ Add draw" />
      {ledgerPanel(
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><Th>Date</Th><Th>Partner</Th><Th>Description</Th><Th align="right">Amount</Th><Th align="right"></Th></tr></thead>
          <tbody>
            {(!rows || rows.length === 0) && <tr><td colSpan={5} style={{ padding: '18px', textAlign: 'center', color: C.dim, fontSize: '12px' }}>No draws yet.</td></tr>}
            {(rows ?? []).map((r: any) => (
              <tr key={r.id}>
                <Td mono muted>{formatDate(r.drawn_on)}</Td>
                <Td>{r.partner}</Td>
                <Td muted>{r.description || '—'}</Td>
                <Td align="right" mono>{formatMoney(r.amount)}</Td>
                <Td align="right"><span style={{ display: 'inline-flex', gap: '8px' }}>
                  <button style={iconBtn} onClick={() => setModal({ mode: 'edit', row: r })} aria-label="Edit"><Icons.edit size={15} /></button>
                  <button style={{ ...iconBtn, color: C.red }} onClick={() => onDelete(r.id)} aria-label="Delete"><Icons.delete size={15} /></button>
                </span></Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {modal && (
        <LedgerModal title={modal.mode === 'add' ? 'Add draw' : 'Edit draw'} busy={busy} onClose={() => setModal(null)}
          fields={[
            { key: 'drawn_on', label: 'Date', type: 'date', default: modal.row?.drawn_on ?? today() },
            { key: 'partner', label: 'Partner', type: 'select', options: ['Daryl', 'Adrian'], default: modal.row?.partner ?? 'Daryl' },
            { key: 'amount', label: 'Amount', type: 'number', default: modal.row?.amount ?? '' },
            { key: 'description', label: 'Description', type: 'text', default: modal.row?.description ?? '' },
            { key: 'notes', label: 'Notes', type: 'text', default: modal.row?.notes ?? '' },
          ]}
          onSave={(vals, key) => { if (modal.mode === 'add') onAdd(vals, key); else onEdit(modal.row.id, vals); setModal(null); }} />
      )}
    </div>
  );
}

// ---- Adjustments -----------------------------------------------------------

/**
 * Every lead id a set of ledger data mentions (`lead_id` / `leadId`, at any
 * depth): the jobs this page has to label.
 */
function collectLeadIds(sources: any[]): string[] {
  const out = new Set<string>();
  const walk = (v: any, depth: number) => {
    if (!v || depth > 6) return;
    if (Array.isArray(v)) { for (const x of v) walk(x, depth + 1); return; }
    if (typeof v !== 'object') return;
    for (const [k, x] of Object.entries(v)) {
      if ((k === 'lead_id' || k === 'leadId') && typeof x === 'string' && x) out.add(x);
      else if (x && typeof x === 'object') walk(x, depth + 1);
    }
  };
  for (const src of sources) walk(src, 0);
  return [...out];
}

function AdjustmentsTab({ rows, leadLabel, busy, onAdd, onEdit, onDelete }: any) {
  const [modal, setModal] = useState<{ mode: 'add' | 'edit'; row?: any } | null>(null);
  return (
    <div>
      <TabHeader title="Corrections" onAdd={() => setModal({ mode: 'add' })} addLabel="+ Add correction" />
      {ledgerPanel(
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><Th>Date</Th><Th>Job</Th><Th>Description</Th><Th align="right">Bank Δ</Th><Th align="right">Obl Δ</Th><Th align="right">Adrian Δ</Th><Th align="right">Daryl Δ</Th><Th align="right"></Th></tr></thead>
          <tbody>
            {(!rows || rows.length === 0) && <tr><td colSpan={8} style={{ padding: '18px', textAlign: 'center', color: C.dim, fontSize: '12px' }}>No corrections yet.</td></tr>}
            {(rows ?? []).map((r: any) => (
              <tr key={r.id}>
                <Td mono muted>{formatDate(r.adjusted_on)}</Td>
                <Td muted>{r.lead_id ? leadLabel(r.lead_id) : '—'}</Td>
                <Td>{r.description}</Td>
                <Td align="right" mono>{deltaStr(r.bank_delta)}</Td>
                <Td align="right" mono>{deltaStr(r.contractor_obligation_delta)}</Td>
                <Td align="right" mono>{deltaStr(r.adrian_balance_delta)}</Td>
                <Td align="right" mono>{deltaStr(r.daryl_balance_delta)}</Td>
                <Td align="right"><span style={{ display: 'inline-flex', gap: '8px' }}>
                  <button style={iconBtn} onClick={() => setModal({ mode: 'edit', row: r })} aria-label="Edit"><Icons.edit size={15} /></button>
                  <button style={{ ...iconBtn, color: C.red }} onClick={() => onDelete(r.id)} aria-label="Delete"><Icons.delete size={15} /></button>
                </span></Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {modal && (
        <LedgerModal title={modal.mode === 'add' ? 'Add correction' : 'Edit correction'} busy={busy} onClose={() => setModal(null)}
          showAdjustmentPreview
          fields={[
            { key: 'adjusted_on', label: 'Date', type: 'date', default: modal.row?.adjusted_on ?? today() },
            { key: 'description', label: 'Description (required)', type: 'text', default: modal.row?.description ?? '', required: true },
            { key: 'bank_delta', label: 'Bank Δ', type: 'number', default: modal.row?.bank_delta ?? 0 },
            { key: 'contractor_obligation_delta', label: 'Obligations Δ', type: 'number', default: modal.row?.contractor_obligation_delta ?? 0 },
            { key: 'adrian_balance_delta', label: 'Adrian Δ', type: 'number', default: modal.row?.adrian_balance_delta ?? 0 },
            { key: 'daryl_balance_delta', label: 'Daryl Δ', type: 'number', default: modal.row?.daryl_balance_delta ?? 0 },
            { key: 'lead_id', label: 'Job # (optional — counts in that job\'s Margin)', type: 'job', default: modal.row?.lead_id ?? '' },
            { key: 'notes', label: 'Notes', type: 'text', default: modal.row?.notes ?? '' },
          ]}
          onSave={(vals, key) => { if (modal.mode === 'add') onAdd(vals, key); else onEdit(modal.row.id, vals); setModal(null); }} />
      )}
    </div>
  );
}

// ---- Contractor payments ---------------------------------------------------

/**
 * Contractor Payments (lib/ledger.ts computeContractorLedger).
 *
 * Per contractor: Obligation (unpaid pay on Invoiced jobs), Owed now (unpaid
 * pay on jobs an admin has Closed), Paid ahead (paid beyond what is owed; only
 * shown when someone has some), Balance. One total per job: pay + tip +
 * corrections. Nothing counts before the invoice.
 *
 * Expanded: the active jobs — Invoiced, or Closed with something unpaid — each
 * opening its breakdown. Closed and fully paid jobs, and every payment, are in
 * the "Paid jobs" popup.
 *
 * The balances must add up to the Contractors figure on the Overview. The
 * server checks that from the same data; the page says so only when it fails.
 */
function ContractorPaymentsTab({ ledger, rows, leadLabel, busy, onPay, onDelete }: any) {
  const [payFor, setPayFor] = useState<any | null>(null);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [paidFor, setPaidFor] = useState<any | null>(null);
  const [jobFor, setJobFor] = useState<{ row: any; job: any } | null>(null);

  const rec = ledger?.reconciliation;
  const totals = ledger?.totals;
  const ledgerRows: any[] = ledger?.rows ?? [];
  const notApproved: any[] = ledger?.notApproved ?? [];
  const unattributed = num(ledger?.unattributedAdjustments);
  const totalOwed = num(totals?.balance) + unattributed;
  const showAhead = ledgerRows.some((r) => num(r.paidAhead) !== 0);
  const nameOf = (id: string | null) => ledgerRows.find((r) => r.contractorId === id)?.name ?? 'No contractor';

  const paymentsFor = (contractorId: string | null) =>
    (rows ?? []).filter((r: any) => (r.contractor_id ?? null) === contractorId);
  const cols = 5 + (showAhead ? 1 : 0);

  return (
    <div>
      <TabHeader title="Contractor Payments" />

      {rec && !rec.ties && (
        <div style={{ background: `${C.redTint}`, border: `2px solid ${C.redLine}`, color: C.red, borderRadius: '9px', padding: '12px 16px', fontSize: '13px', fontWeight: 700, marginBottom: '14px' }}>
          <Icons.warning size={13} style={{ verticalAlign: 'text-bottom' }} /> Contractor balances do not match the books — off by {formatMoney(Math.abs(num(rec.diff)))}.
          <div style={{ fontSize: '11px', fontWeight: 400, marginTop: '4px', fontFamily: C.mono }}>
            Balances {formatMoney(rec.sumOfBalances)}{num(rec.unattributedAdjustments) ? ` + corrections not tied to a contractor ${formatMoney(rec.unattributedAdjustments)}` : ''} = {formatMoney(rec.total)} ≠ Contractors on the Overview {formatMoney(rec.obligation)}
          </div>
        </div>
      )}

      {notApproved.length > 0 && (
        <div style={{ background: `${C.amberTint}`, border: `1px solid ${C.amberSoftLine}`, borderRadius: '10px', padding: '12px 14px', marginBottom: '14px' }}>
          <div style={{ fontSize: '12px', fontWeight: 700, color: C.amber, marginBottom: '4px' }}>
            Invoiced, contractor pay not approved — {notApproved.length} job{notApproved.length === 1 ? '' : 's'}
          </div>
          <div style={{ fontSize: '11px', color: C.muted, marginBottom: '6px' }}>
            Nothing is owed to the contractor for these until their pay is approved on the job's Dispatch tab.
          </div>
          {notApproved.map((n) => (
            <div key={n.leadId} style={{ display: 'flex', gap: '12px', alignItems: 'center', fontSize: '12px', padding: '3px 0', flexWrap: 'wrap' }}>
              <span style={{ fontFamily: C.mono, color: C.muted, minWidth: '90px' }}>{formatDate(n.invoiceDate)}</span>
              <a href={`/lead/${encodeURIComponent(n.leadId)}?tab=dispatch`} target="_blank" rel="noreferrer" style={{ color: C.text, minWidth: '220px', textDecoration: 'none' }}>{leadLabel(n.leadId)} ↗</a>
              <span style={{ color: C.muted }}>{n.contractorId ? nameOf(n.contractorId) : 'No contractor assigned'}</span>
              <span style={{ color: C.dim }}>{n.ceStatus ? `estimate ${humanize(n.ceStatus).toLowerCase()}` : 'no contractor estimate'}</span>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '12px' }}>
        <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '14px 18px', minWidth: '200px' }}>
          <div style={{ fontSize: '10px', color: C.dim, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 700 }}>Total owed to contractors</div>
          <div style={{ fontSize: '24px', fontWeight: 700, fontFamily: C.mono, color: totalOwed > 0 ? C.amber : C.green, marginTop: '3px' }}>{formatMoney(totalOwed)}</div>
          {(splitLine(totalOwed, totals) ?? []).map((line) => (
            <div key={line} style={{ fontSize: '11px', color: C.muted, marginTop: '3px', fontFamily: C.mono, whiteSpace: 'nowrap' }}>{line}</div>
          ))}
        </div>
      </div>

      {ledgerPanel(
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <Th>Contractor</Th><Th align="right">Obligation</Th><Th align="right">Owed now</Th>
              {showAhead && <Th align="right">Paid ahead</Th>}
              <Th align="right">Balance</Th><Th align="right"></Th>
            </tr>
          </thead>
          <tbody>
            {ledgerRows.length === 0 && (
              <tr><td colSpan={cols} style={{ padding: '18px', textAlign: 'center', color: C.dim, fontSize: '12px' }}>
                {ledger ? 'No contractors on file.' : 'Loading…'}
              </td></tr>
            )}
            {ledgerRows.map((r: any) => {
              const key = r.contractorId ?? '_none';
              const expanded = !!open[key];
              const active = activeJobsOf(r);
              const nPaid = paidJobsOf(r).length;
              const nPayments = paymentsFor(r.contractorId).length;
              return (
                <React.Fragment key={key}>
                  <tr>
                    <Td>
                      <button onClick={() => setOpen((p) => ({ ...p, [key]: !p[key] }))}
                        style={{ background: 'none', border: 'none', padding: 0, color: C.text, fontSize: '12px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                        title={expanded ? 'Hide active jobs' : 'Show active jobs'}>
                        <span style={{ color: C.dim, fontSize: '9px' }}>{expanded ? '▼' : '▶'}</span>
                        {r.name}
                        <span style={{ color: C.dim, fontSize: '11px' }}>{active.length ? `· ${active.length} active` : ''}</span>
                        {!r.active && <span style={{ fontSize: '9px', color: C.dim, border: `1px solid ${C.border}`, borderRadius: '4px', padding: '0 4px' }}>inactive</span>}
                      </button>
                    </Td>
                    <Td align="right" mono muted>{moneyOrDash(r.obligation)}</Td>
                    <Td align="right" mono color={r.owedNow > 0 ? C.amber : C.muted}>{moneyOrDash(r.owedNow)}</Td>
                    {showAhead && <Td align="right" mono muted>{num(r.paidAhead) ? formatMoney(r.paidAhead) : ''}</Td>}
                    <Td align="right" mono color={r.balance > 0 ? C.amber : r.balance < 0 ? C.red : C.muted}>{formatMoney(r.balance)}</Td>
                    <Td align="right">
                      <span style={{ display: 'inline-flex', gap: '6px', alignItems: 'center' }}>
                        {(nPaid > 0 || nPayments > 0) && (
                          <button onClick={() => setPaidFor(r)} style={{ ...iconBtn, color: C.accent, fontSize: '11px', fontWeight: 600 }}>Paid jobs{nPaid ? ` (${nPaid})` : ''}</button>
                        )}
                        {r.contractorId && (
                          <button onClick={() => setPayFor(r)} disabled={busy} style={{ ...addBtn, padding: '5px 10px', fontSize: '11px' }}>Record payment</button>
                        )}
                      </span>
                    </Td>
                  </tr>
                  {expanded && (
                    <tr>
                      <td colSpan={cols} style={{ padding: '6px 12px 12px 30px', background: C.panel2 }}>
                        {active.length === 0 && <div style={{ fontSize: '11px', color: C.dim, padding: '6px 0' }}>No active jobs.</div>}
                        {active.map((j: any) => (
                          <JobLine key={j.opening ? 'opening' : j.leadId} job={j} leadLabel={leadLabel} onOpen={() => setJobFor({ row: r, job: j })} />
                        ))}
                        {num(r.otherAdjustments) !== 0 && (
                          <div style={{ fontSize: '11px', color: C.muted, paddingTop: '6px' }}>
                            Corrections on jobs that are not invoiced: <span style={{ fontFamily: C.mono }}>{formatMoney(r.otherAdjustments)}</span>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      )}

      {payFor && (
        <RecordContractorPaymentModal contractor={payFor} leadLabel={leadLabel} busy={busy}
          onSave={(d: any, key: string) => { onPay(d, key); setPayFor(null); }}
          onClose={() => setPayFor(null)} />
      )}
      {paidFor && (
        <PaidJobsModal row={paidFor} jobs={paidJobsOf(paidFor)} payments={paymentsFor(paidFor.contractorId)} leadLabel={leadLabel} busy={busy}
          jobKey={(j: any) => (j.opening ? 'opening' : j.leadId)}
          onOpenJob={(j: any) => setJobFor({ row: paidFor, job: j })} onDelete={onDelete} onClose={() => setPaidFor(null)} />
      )}
      {jobFor && (
        <JobBreakdownModal row={jobFor.row} job={jobFor.job} leadLabel={leadLabel}
          payments={paymentsFor(jobFor.row.contractorId).filter((p: any) => String(p.lead_id ?? '') === String(jobFor.job.leadId))}
          onClose={() => setJobFor(null)} />
      )}
    </div>
  );
}

const moneyOrDash = (v: unknown) => (num(v) ? formatMoney(num(v)) : '—');

// Ledger jobs (contractor or commission). Active = Invoiced (paid or not), or
// Closed with something unpaid; Paid = Closed and fully paid.
const allJobsOf = (r: any): any[] => [...(r?.jobs ?? []), ...(r?.paidJobs ?? [])];
const activeJobsOf = (r: any) => allJobsOf(r).filter((j) => j.status === 'invoiced' || num(j.remaining) > 0.005)
  .sort((a, b) => String(a.date ?? '').localeCompare(String(b.date ?? '')));
const paidJobsOf = (r: any) => allJobsOf(r).filter((j) => j.status === 'closed' && num(j.remaining) <= 0.005)
  .sort((a, b) => String(b.date ?? '').localeCompare(String(a.date ?? '')));

/** One active job in an expanded row: date, job, chip, total, what is left. Opens the breakdown. */
function JobLine({ job: j, leadLabel, onOpen, detail }: { job: any; leadLabel: (id: string) => string; onOpen: () => void; detail?: string }) {
  return (
    <button onClick={onOpen}
      style={{ display: 'flex', width: '100%', gap: '12px', alignItems: 'center', fontSize: '12px', padding: '6px 0', background: 'none', border: 'none', borderBottom: `1px solid ${C.border}`, cursor: 'pointer', textAlign: 'left' }}>
      <span style={{ color: C.muted, fontFamily: C.mono, minWidth: '90px' }}>{formatDate(j.date)}</span>
      <span style={{ color: C.text, minWidth: '220px' }}>{jobName(j, leadLabel)}</span>
      <StatusChip job={j} />
      {detail && <span style={{ color: C.dim, fontSize: '11px' }}>{detail}</span>}
      <span style={{ marginLeft: 'auto', color: C.text, fontFamily: C.mono }}>{formatMoney(j.total)}</span>
      <span style={{ color: C.dim, fontFamily: C.mono, fontSize: '11px', minWidth: '120px', textAlign: 'right' }}>
        {num(j.remaining) <= 0.005 ? 'paid' : num(j.remaining) < num(j.total) ? `${formatMoney(j.remaining)} left` : ''}
      </span>
    </button>
  );
}

function BreakdownLine({ label, v, text, strong, neg, muted }: { label: string; v?: number; text?: string; strong?: boolean; neg?: boolean; muted?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', padding: '4px 0', borderTop: strong ? `1px solid ${C.border}` : undefined, fontWeight: strong ? 700 : 400 }}>
      <span style={{ color: muted ? C.muted : C.text }}>{label}</span>
      <span style={{ fontFamily: C.mono, color: muted ? C.muted : C.text }}>{text ?? (neg ? `−${formatMoney(num(v))}` : formatMoney(num(v)))}</span>
    </div>
  );
}

/** The bottom of a job breakdown: what paid it, what is left, its payments, a link to the job. */
function JobPaidLines({ job, payments, link }: { job: any; payments: any[]; link?: { href: string; label: string } }) {
  return (
    <>
      {num(job.paidDirect) !== 0 && <BreakdownLine label="Paid on this job" v={num(job.paidDirect)} neg muted />}
      {num(job.paidOnAccount) !== 0 && <BreakdownLine label="Covered by payments on account" v={num(job.paidOnAccount)} neg muted />}
      <BreakdownLine label={num(job.remaining) > 0.005 ? (job.status === 'closed' ? 'Left to pay · owed now' : 'Left to pay · owed once Closed') : 'Left to pay'} v={num(job.remaining)} strong />

      {!job.opening && <div style={{ fontSize: '10px', color: C.dim, textTransform: 'uppercase', fontWeight: 700, padding: '14px 0 6px' }}>Payments on this job</div>}
      {!job.opening && payments.length === 0 && <div style={{ fontSize: '11px', color: C.dim }}>None tied to this job.</div>}
      {payments.map((p: any) => (
        <div key={p.id} style={{ display: 'flex', gap: '10px', fontSize: '11px', padding: '3px 0' }}>
          <span style={{ color: C.muted, fontFamily: C.mono, minWidth: '90px' }}>{formatDate(p.paid_on)}</span>
          <span style={{ color: C.muted }}>{p.method || '—'}</span>
          <span style={{ color: C.dim }}>{p.notes || ''}</span>
          <span style={{ marginLeft: 'auto', fontFamily: C.mono, color: C.text }}>{formatMoney(p.amount)}</span>
        </div>
      ))}
      {link && (
        <div style={{ marginTop: '14px', textAlign: 'right' }}>
          <a href={link.href} target="_blank" rel="noreferrer" style={{ color: C.accent, fontSize: '12px', textDecoration: 'none' }}>{link.label} ↗</a>
        </div>
      )}
    </>
  );
}

/** A ledger line's name: the job, or the opening balance carried from the books. */
const jobName = (j: any, leadLabel: (id: string) => string) => (j.opening ? 'Opening balance (from the books)' : leadLabel(j.leadId));

function StatusChip({ job }: { job: any }) {
  const closed = job.status === 'closed';
  if (job.opening) {
    return (
      <span style={{ fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', padding: '2px 7px', borderRadius: '10px', whiteSpace: 'nowrap',
        color: C.amber, background: `${C.amberTint}`, border: `1px solid ${C.amberSoftLine}` }}>
        Opening · owed now
      </span>
    );
  }
  return (
    <span style={{ fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', padding: '2px 7px', borderRadius: '10px', whiteSpace: 'nowrap',
      color: closed ? C.amber : C.accent, background: closed ? `${C.amberTint}` : `${C.accentTint}`, border: `1px solid ${tintOf(closed ? C.amberLine : C.accent, 33)}` }}>
      {closed ? 'Closed · owed now' : 'Invoiced'}
    </span>
  );
}

function ModalFrame({ title, sub, width = '560px', onClose, children }: { title: string; sub?: React.ReactNode; width?: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'var(--crm-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: '12px', padding: '18px', width, maxWidth: '94vw', maxHeight: '88vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: sub ? '4px' : '12px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: 700, color: C.text }}>{title}</h3>
          <button onClick={onClose} style={{ ...iconBtn, fontSize: '16px' }} aria-label="Close">✕</button>
        </div>
        {sub && <div style={{ fontSize: '11px', color: C.muted, marginBottom: '14px' }}>{sub}</div>}
        {children}
      </div>
    </div>
  );
}

/** The opening balance's own breakdown: what was carried over, what has paid it. */
function OpeningBreakdown({ row, job, onClose }: { row: any; job: any; onClose: () => void }) {
  return (
    <ModalFrame title={`Opening balance — ${row.name}`} onClose={onClose}
      sub={<span style={{ display: 'inline-flex', gap: '8px', alignItems: 'center' }}>Carried from the monthly books at the cutover ({formatDate(job.date)}) <StatusChip job={job} /></span>}>
      <BreakdownLine label="Owed at the cutover" v={num(job.total)} strong />
      <JobPaidLines job={job} payments={[]} />
      <div style={{ fontSize: '11px', color: C.muted, marginTop: '10px' }}>
        Payments recorded on account pay this first. It is one figure from the books, not split by job.
      </div>
    </ModalFrame>
  );
}

/** One job's contractor total, line by line, and what has paid it. */
function JobBreakdownModal({ row, job, payments, leadLabel, onClose }: any) {
  if (job.opening) return <OpeningBreakdown row={row} job={job} onClose={onClose} />;
  return (
    <ModalFrame title={leadLabel(job.leadId)} onClose={onClose}
      sub={<span style={{ display: 'inline-flex', gap: '8px', alignItems: 'center' }}>{row.name} · invoiced {formatDate(job.date)} <StatusChip job={job} /></span>}>
      <BreakdownLine label="Contractor pay" v={num(job.pay)} />
      {num(job.tip) !== 0 && <BreakdownLine label="Tip (100% to the crew)" v={num(job.tip)} />}
      {num(job.adjustments) !== 0 && <BreakdownLine label="Corrections" v={num(job.adjustments)} />}
      <BreakdownLine label="Total for this job" v={num(job.total)} strong />
      <JobPaidLines job={job} payments={payments}
        link={{ href: `/lead/${encodeURIComponent(job.leadId)}?tab=dispatch`, label: "Open the job's Dispatch tab" }} />
    </ModalFrame>
  );
}

/** Closed and fully paid jobs (a contractor's or a rep's), and every payment made to them. */
function PaidJobsModal({ row, jobs, payments, leadLabel, busy, jobKey, onOpenJob, onDelete, onClose }: any) {
  const paidTotal = payments.reduce((t: number, p: any) => t + num(p.amount), 0);
  return (
    <ModalFrame title={`Paid jobs — ${row.name}`} width="680px" onClose={onClose}
      sub={`${jobs.length} paid job${jobs.length === 1 ? '' : 's'} · ${payments.length} payment${payments.length === 1 ? '' : 's'} totalling ${formatMoney(paidTotal)}`}>
      <div style={{ fontSize: '10px', color: C.dim, textTransform: 'uppercase', fontWeight: 700, paddingBottom: '6px' }}>Jobs</div>
      {jobs.length === 0 && <div style={{ fontSize: '11px', color: C.dim, paddingBottom: '6px' }}>No Closed job is fully paid yet.</div>}
      {jobs.map((j: any) => (
        <button key={jobKey(j)} onClick={() => onOpenJob(j)}
          style={{ display: 'flex', width: '100%', gap: '12px', alignItems: 'center', fontSize: '12px', padding: '5px 0', background: 'none', border: 'none', borderBottom: `1px solid ${C.border}`, cursor: 'pointer', textAlign: 'left' }}>
          <span style={{ color: C.muted, fontFamily: C.mono, minWidth: '90px' }}>{formatDate(j.date)}</span>
          <span style={{ color: C.text }}>{jobName(j, leadLabel)}</span>
          <span style={{ marginLeft: 'auto', color: C.text, fontFamily: C.mono }}>{formatMoney(j.total)}</span>
        </button>
      ))}

      <div style={{ fontSize: '10px', color: C.dim, textTransform: 'uppercase', fontWeight: 700, padding: '16px 0 6px' }}>Payments</div>
      {payments.length === 0 && <div style={{ fontSize: '11px', color: C.dim }}>Nothing paid yet.</div>}
      {[...payments].sort((a: any, b: any) => String(b.paid_on ?? '').localeCompare(String(a.paid_on ?? ''))).map((p: any) => (
        <div key={p.id} style={{ display: 'flex', gap: '10px', alignItems: 'center', fontSize: '11px', padding: '3px 0' }}>
          <span style={{ color: C.muted, fontFamily: C.mono, minWidth: '90px' }}>{formatDate(p.paid_on)}</span>
          <span style={{ color: p.lead_id ? C.text : C.dim, minWidth: '220px' }}>{p.lead_id ? leadLabel(p.lead_id) : (p.label ?? 'On account')}</span>
          <span style={{ color: C.muted }}>{p.method || '—'}</span>
          <span style={{ color: C.dim }}>{p.notes || ''}</span>
          <span style={{ color: C.text, fontFamily: C.mono, marginLeft: 'auto' }}>{formatMoney(p.amount)}</span>
          <button style={{ ...iconBtn, color: C.red }} disabled={busy} onClick={() => onDelete(p.id)} aria-label="Delete payment"><Icons.delete size={14} /></button>
        </div>
      ))}
    </ModalFrame>
  );
}

/**
 * Record a contractor payment.
 *
 * ALLOCATION IS OPTIONAL AND MUST ADD UP. One e-transfer settling three jobs
 * writes three rows sharing a reference, because contractor_estimate_id is a
 * single column and one row naming three jobs cannot say how much went to each.
 * Leaving the allocation empty records a payment on account, which is a thing
 * that genuinely happens and which the old lead-scoped form could not express
 * at all. The server rejects allocations that do not sum to the payment; this
 * says so before you submit.
 */
function RecordContractorPaymentModal({ contractor, leadLabel, busy, onSave, onClose }: any) {
  const intentKey = useIntentKey(); // one opening = one payment
  const [amount, setAmount] = useState('');
  const [paidOn, setPaidOn] = useState(new Date().toISOString().slice(0, 10));
  const [method, setMethod] = useState('');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [allocs, setAllocs] = useState<{ lead_id: string; amount: string }[]>([]);

  // Only this contractor's approved jobs are offerable — allocating a payment
  // to a job that is not theirs would put their money on someone else's ledger.
  const jobs: any[] = (contractor.jobs ?? []).filter((j: any) => !j.opening); // the opening balance is paid on account
  const total = Number(amount) || 0;
  const allocSum = allocs.reduce((t, a) => t + (Number(a.amount) || 0), 0);
  const allocated = allocs.filter((a) => a.lead_id && Number(a.amount) > 0);
  const mismatch = allocated.length > 0 && Math.abs(allocSum - total) >= 0.005;
  const canSave = total > 0 && !mismatch && !busy;

  const input: React.CSSProperties = { width: '100%', padding: '7px 9px', background: C.panel2, border: `1px solid ${C.border}`, color: C.text, borderRadius: '6px', fontSize: '12px' };
  const label: React.CSSProperties = { fontSize: '10px', color: C.muted, display: 'block', marginBottom: '3px' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'var(--crm-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
      <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: '12px', padding: '18px', width: '560px', maxHeight: '86vh', overflowY: 'auto' }}>
        <h3 style={{ fontSize: '14px', fontWeight: 700, color: C.text, marginBottom: '4px' }}>Record payment — {contractor.name}</h3>
        <div style={{ fontSize: '11px', color: C.muted, marginBottom: '14px' }}>
          Balance {formatMoney(contractor.balance)} · Obligation {formatMoney(contractor.obligation)} · Owed now {formatMoney(contractor.owedNow)}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
          <div><label style={label}>Amount</label><input style={input} {...numberInput(1)} value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus /></div>
          <div><label style={label}>Date paid</label><input style={input} type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} /></div>
          <div><label style={label}>Method</label><input style={input} value={method} onChange={(e) => setMethod(e.target.value)} placeholder="e-Transfer" /></div>
          <div><label style={label}>Reference</label><input style={input} value={reference} onChange={(e) => setReference(e.target.value)} placeholder="shared by every job on this payment" /></div>
        </div>
        <div style={{ marginBottom: '12px' }}><label style={label}>Notes</label><input style={input} value={notes} onChange={(e) => setNotes(e.target.value)} /></div>

        <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
            <span style={{ fontSize: '10px', color: C.dim, textTransform: 'uppercase', fontWeight: 700 }}>Allocate to jobs (optional)</span>
            <button
              onClick={() => setAllocs((a) => [...a, { lead_id: jobs[0]?.leadId ?? '', amount: '' }])}
              disabled={jobs.length === 0}
              style={{ ...iconBtn, color: jobs.length ? C.accent : C.dim, fontSize: '11px' }}
            >
              + Add job
            </button>
          </div>
          {allocs.length === 0 && (
            <div style={{ fontSize: '11px', color: C.dim, paddingBottom: '4px' }}>
              {jobs.length === 0
                ? 'This contractor has no unpaid invoiced jobs, so the payment will be recorded on account (paid ahead).'
                : 'Leave empty to record a payment on account, not yet tied to a job.'}
            </div>
          )}
          {allocs.map((a, i) => (
            <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '6px' }}>
              <select style={{ ...input, flex: 1 }} value={a.lead_id} onChange={(e) => setAllocs((p) => p.map((x, j) => (j === i ? { ...x, lead_id: e.target.value } : x)))}>
                <option value="">— pick a job —</option>
                {jobs.map((j) => (
                  <option key={j.leadId} value={j.leadId}>{leadLabel(j.leadId)} · {formatMoney(j.remaining)} unpaid{j.status === 'closed' ? ' (owed now)' : ''}</option>
                ))}
              </select>
              <input style={{ ...input, width: '110px' }} {...numberInput(1)} placeholder="amount" value={a.amount} onChange={(e) => setAllocs((p) => p.map((x, j) => (j === i ? { ...x, amount: e.target.value } : x)))} />
              <button style={{ ...iconBtn, color: C.red }} onClick={() => setAllocs((p) => p.filter((_, j) => j !== i))} aria-label="Remove"><Icons.delete size={14} /></button>
            </div>
          ))}
          {allocated.length > 0 && (
            <div style={{ fontSize: '11px', fontFamily: C.mono, color: mismatch ? C.red : C.green, marginTop: '4px' }}>
              allocated {formatMoney(allocSum)} of {formatMoney(total)}{mismatch ? ' — these have to match' : ''}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
          <button onClick={onClose} style={{ padding: '8px 14px', background: C.panel2, color: C.muted, border: `1px solid ${C.border}`, borderRadius: '7px', cursor: 'pointer', fontSize: '12px' }}>Cancel</button>
          <button
            disabled={!canSave}
            onClick={() => onSave({
              contractor_id: contractor.contractorId,
              amount: total,
              paid_on: paidOn,
              method: method.trim() || null,
              notes: notes.trim() || null,
              reference: reference.trim() || null,
              allocations: allocated.length ? allocated.map((a) => ({ lead_id: a.lead_id, amount: Number(a.amount) })) : undefined,
            }, intentKey)}
            style={{ ...addBtn, opacity: canSave ? 1 : 0.5, cursor: canSave ? 'pointer' : 'default' }}
          >
            Record payment
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- Generic ledger modal --------------------------------------------------

interface Field {
  key: string; label: string; type: 'text' | 'number' | 'date' | 'select' | 'job';
  options?: (string | { value: string; label: string })[]; default?: any; required?: boolean;
}

function LedgerModal({ title, fields, busy, showAdjustmentPreview, onSave, onClose }: {
  title: string; fields: Field[]; busy: boolean; showAdjustmentPreview?: boolean;
  onSave: (vals: Record<string, any>, idempotencyKey: string) => void; onClose: () => void;
}) {
  const [vals, setVals] = useState<Record<string, any>>(() => {
    const v: Record<string, any> = {};
    for (const f of fields) v[f.key] = f.default ?? '';
    return v;
  });
  const set = (k: string, val: any) => setVals((s) => ({ ...s, [k]: val }));

  const requiredMissing = fields.some((f) => f.required && !String(vals[f.key] ?? '').trim());

  const intentKey = useIntentKey(); // one opening = one intent
  const save = () => {
    const out: Record<string, any> = {};
    for (const f of fields) {
      const raw = vals[f.key];
      if (f.type === 'number') out[f.key] = raw === '' || raw == null ? 0 : Number(raw);
      else out[f.key] = raw === '' ? null : raw;
    }
    onSave(out, intentKey);
  };

  const input: React.CSSProperties = { width: '100%', padding: '7px 9px', background: C.panel2, border: `1px solid ${C.border}`, color: C.text, borderRadius: '6px', fontSize: '12px' };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'var(--crm-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: '12px', padding: '20px', width: '440px', maxWidth: '92vw', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: '14px', color: C.text }}>{title}</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          {fields.map((f) => (
            <label key={f.key} style={{ display: 'flex', flexDirection: 'column', gap: '3px', gridColumn: (f.type === 'text' && f.key !== 'partner') || f.type === 'job' ? '1 / -1' : 'auto' }}>
              <span style={{ fontSize: '10px', color: C.muted }}>{f.label}</span>
              {f.type === 'job' ? (
                <JobPicker value={vals[f.key] ?? ''} onChange={(v) => set(f.key, v)} style={input} />
              ) : f.type === 'select' ? (
                <select value={vals[f.key] ?? ''} onChange={(e) => set(f.key, e.target.value)} style={input}>
                  <option value="">—</option>
                  {(f.options ?? []).map((o) => {
                    const val = typeof o === 'string' ? o : o.value;
                    const lab = typeof o === 'string' ? o : o.label;
                    return <option key={val} value={val}>{lab}</option>;
                  })}
                </select>
              ) : (
                <input {...(f.type === 'number' ? numberInput(1, { negative: true }) : { type: f.type })} value={vals[f.key] ?? ''} onChange={(e) => set(f.key, e.target.value)} style={input} />
              )}
            </label>
          ))}
        </div>

        {showAdjustmentPreview && (
          <div style={{ marginTop: '12px', padding: '10px', background: C.panel2, borderRadius: '7px', fontSize: '11px', color: C.muted, fontFamily: C.mono }}>
            This correction will change: bank {deltaStr(vals.bank_delta)} · obligations {deltaStr(vals.contractor_obligation_delta)} · Adrian {deltaStr(vals.adrian_balance_delta)} · Daryl {deltaStr(vals.daryl_balance_delta)}
            <div style={{ marginTop: '4px', color: Math.abs(num(vals.bank_delta) - (num(vals.contractor_obligation_delta) + num(vals.adrian_balance_delta) + num(vals.daryl_balance_delta))) < 0.01 ? C.green : C.amber }}>
              {Math.abs(num(vals.bank_delta) - (num(vals.contractor_obligation_delta) + num(vals.adrian_balance_delta) + num(vals.daryl_balance_delta))) < 0.01
                ? 'Balanced (bank Δ = obl Δ + Adrian Δ + Daryl Δ)'
                : 'Unbalanced — this will make the books not reconcile.'}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
          <button onClick={onClose} style={{ padding: '7px 14px', background: C.panel2, border: `1px solid ${C.border}`, color: C.muted, borderRadius: '7px', cursor: 'pointer', fontSize: '12px' }}>Cancel</button>
          <button onClick={save} disabled={busy || requiredMissing} style={{ ...addBtn, opacity: busy || requiredMissing ? 0.5 : 1 }}>Save</button>
        </div>
      </div>
    </div>
  );
}

// ---- Monthly View (Task B) -------------------------------------------------

function MonthlyTab() {
  const api = useApiClient();
  const { data: months } = useApi<any[]>(async () => (await api.getAccountingMonthly()).data || []);
  const [expanded, setExpanded] = useState<string | null>(null);
  const rows = months ?? [];
  const year = String(new Date().getFullYear());

  const sum = (list: any[]) =>
    list.reduce((a, m) => ({ revenue: a.revenue + num(m.revenue), expenses: a.expenses + num(m.expenses), contractorCost: a.contractorCost + num(m.contractorCost), commission: a.commission + num(m.commission), net: a.net + num(m.net), daryl: a.daryl + num(m.daryl), adrian: a.adrian + num(m.adrian) }), { revenue: 0, expenses: 0, contractorCost: 0, commission: 0, net: 0, daryl: 0, adrian: 0 });
  const ytd = sum(rows.filter((m) => String(m.month).startsWith(year)));
  const all = sum(rows);

  const cellR: React.CSSProperties = { padding: '8px 12px', fontSize: '12px', textAlign: 'right', fontFamily: C.mono, color: C.text, borderBottom: `1px solid ${C.panel2}` };

  const summaryRow = (label: string, s: any) => (
    <tr style={{ background: C.panel2 }}>
      <td style={{ padding: '8px 12px', fontSize: '11px', fontWeight: 700, color: C.accent }}>{label}</td>
      <td style={{ ...cellR, fontWeight: 700 }}>{formatMoney(s.revenue)}</td>
      <td style={{ ...cellR, fontWeight: 700 }}>{formatMoney(s.expenses)}</td>
      <td style={{ ...cellR, fontWeight: 700 }}>{formatMoney(s.contractorCost)}</td>
      <td style={{ ...cellR, fontWeight: 700 }}>{formatMoney(s.commission)}</td>
      <td style={{ ...cellR, fontWeight: 700, color: s.net < 0 ? C.red : C.green }}>{formatMoney(s.net)}</td>
      {/* The server's own split for THIS month, not a client-side recompute
          from two literals. The percentages differ by month now. */}
      <td style={cellR}>{formatMoney(round2(num(s.daryl)))}</td>
      <td style={cellR}>{formatMoney(round2(num(s.adrian)))}</td>
      <td style={cellR} />
    </tr>
  );

  return (
    <div>
      <h3 style={{ fontSize: '11px', fontWeight: 700, color: C.dim, textTransform: 'uppercase', marginBottom: '10px' }}>Monthly P&amp;L</h3>
      {ledgerPanel(
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <Th>Month</Th><Th align="right">Revenue</Th><Th align="right">Expenses</Th><Th align="right">Contractor Cost</Th><Th align="right">Commission</Th>
            <Th align="right">Net Profit</Th><Th align="right">Daryl</Th><Th align="right">Adrian</Th><Th />
          </tr></thead>
          <tbody>
            {summaryRow('All-time', all)}
            {summaryRow(`YTD ${year}`, ytd)}
            {rows.length === 0 && <tr><td colSpan={9} style={{ padding: '18px', textAlign: 'center', color: C.dim, fontSize: '12px' }}>No ledger activity yet.</td></tr>}
            {rows.map((m) => (
              <React.Fragment key={m.month}>
                <tr onClick={() => setExpanded(expanded === m.month ? null : m.month)} style={{ cursor: 'pointer' }}>
                  <td style={{ padding: '8px 12px', fontSize: '12px', color: C.text }}>
                    <span style={{ color: C.muted, marginRight: '6px' }}>{expanded === m.month ? '▾' : '▸'}</span>
                    {monthLabel(m.month)}
                    {m.books && <span title="From the monthly books (before the cutover)" style={{ fontSize: '9px', marginLeft: '8px', padding: '1px 6px', borderRadius: '4px', background: `${C.accentTint}`, color: C.accent }}>books</span>}
                    {m.historical && <span style={{ fontSize: '9px', marginLeft: '8px', padding: '1px 6px', borderRadius: '4px', background: `${C.amberTint}`, color: C.amber }}>hist</span>}
                  </td>
                  <td style={cellR}>{formatMoney(m.revenue)}</td>
                  <td style={cellR}>{formatMoney(m.expenses)}</td>
                  <td style={cellR}>{formatMoney(m.contractorCost)}</td>
                  <td style={cellR}>{formatMoney(m.commission ?? 0)}</td>
                  <td style={{ ...cellR, color: m.net < 0 ? C.red : C.text }}>{formatMoney(m.net)}</td>
                  <td style={cellR}>{formatMoney(m.daryl)}</td>
                  <td style={cellR}>{formatMoney(m.adrian)}</td>
                  <td style={cellR} />
                </tr>
                {expanded === m.month && (
                  <tr><td colSpan={9} style={{ padding: 0, background: C.bg }}>
                    <div style={{ padding: '8px 24px 12px' }}>
                      {(m.entries ?? []).length === 0 && <div style={{ fontSize: '11px', color: C.dim, padding: '6px 0' }}>No entries.</div>}
                      {(m.entries ?? []).map((e: any, i: number) => (
                        <div key={i} style={{ display: 'flex', gap: '12px', fontSize: '11px', color: C.muted, padding: '3px 0' }}>
                          <span style={{ fontFamily: C.mono, minWidth: '80px' }}>{formatDate(e.date)}</span>
                          <span style={{ minWidth: '130px', color: C.dim }}>{humanize(e.kind)}</span>
                          <span style={{ flex: 1, color: C.text }}>{e.label}</span>
                          <span style={{ fontFamily: C.mono, color: num(e.amount) < 0 ? C.red : C.text }}>{formatMoney(e.amount)}</span>
                        </div>
                      ))}
                    </div>
                  </td></tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}
      <div style={{ fontSize: '10px', color: C.dim, marginTop: '8px' }}>
        Each figure on its accounting date: deposits on their payment date; at invoice, the total less deposits on the
        invoice date, with contractor pay and commission. Payments after the invoice collect it and are not revenue
        (their card fees are). Net Profit is before draws; the Daryl and Adrian columns use the partner split in
        force for each month (Settings → Pricing &amp; billing). Months marked <b>books</b> come from the monthly books,
        which record income net of Square's cost and contractor pay when it was paid; in the cutover month the books'
        lines and MoveFlow's sit side by side.
      </div>
    </div>
  );
}

function round2(n: number) { return Math.round((n + Number.EPSILON) * 100) / 100; }
